// contexts/voice/useVoiceMessages.ts
// Hook for managing chat messages and transcripts

import { useState, useCallback, useRef } from 'react';
import { ChatMessage } from './types';

interface ToolCall {
  tool: string;
  args: unknown;
}

export function useVoiceMessages() {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [currentUserText, setCurrentUserText] = useState<string>('');
  const [currentAssistantText, setCurrentAssistantText] = useState<string>('');
  const [isTyping, setIsTyping] = useState(false);

  // Refs for current values (needed for WebSocket callbacks)
  const currentUserTextRef = useRef<string>('');
  const currentAssistantTextRef = useRef<string>('');
  const pendingToolCallsRef = useRef<ToolCall[]>([]);

  // Add user transcript text
  const appendUserText = useCallback((text: string) => {
    currentUserTextRef.current += text;
    setCurrentUserText(currentUserTextRef.current);
  }, []);

  // Add assistant response text
  const appendAssistantText = useCallback((text: string) => {
    currentAssistantTextRef.current += text;
    setCurrentAssistantText(currentAssistantTextRef.current);
  }, []);

  // Add a pending tool call (shown at turn completion)
  const addToolCall = useCallback((tool: string, args: unknown) => {
    pendingToolCallsRef.current.push({ tool, args });
  }, []);

  // Complete turn and save messages
  const completeTurn = useCallback(() => {
    const userText = currentUserTextRef.current.trim();
    const assistantText = currentAssistantTextRef.current.trim();
    const toolCalls = [...pendingToolCallsRef.current];

    if (userText || assistantText || toolCalls.length > 0) {
      setMessages(prev => {
        const newMessages = [...prev];
        // 1. User message first
        if (userText) {
          newMessages.push({ role: 'user', content: userText });
        }
        // 2. Tool calls (show what agent did)
        for (const tc of toolCalls) {
          newMessages.push({
            role: 'system',
            content: `🔧 ${tc.tool}`
          });
        }
        // 3. Assistant response
        if (assistantText) {
          newMessages.push({ role: 'assistant', content: assistantText });
        }
        return newMessages;
      });
    }

    // Reset for next turn
    currentUserTextRef.current = '';
    currentAssistantTextRef.current = '';
    pendingToolCallsRef.current = [];
    setCurrentUserText('');
    setCurrentAssistantText('');
    setIsTyping(false);
  }, []);

  // Reset all messages (for new session)
  const resetMessages = useCallback(() => {
    setMessages([]);
    currentUserTextRef.current = '';
    currentAssistantTextRef.current = '';
    pendingToolCallsRef.current = [];
    setCurrentUserText('');
    setCurrentAssistantText('');
    setIsTyping(false);
  }, []);

  return {
    // State
    messages,
    currentUserText,
    currentAssistantText,
    isTyping,

    // Actions
    appendUserText,
    appendAssistantText,
    addToolCall,
    completeTurn,
    resetMessages,
    setIsTyping,
  };
}
